import { useCallback, useEffect, useState } from "react";
import { Radio, Plus, Trash2, CalendarClock, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const ACCESS = [
  { v: "public", label: "Public preview" },
  { v: "subscriber", label: "Registered subscriber" },
  { v: "member", label: "Paid member (demo)" },
];

function fmt(iso) {
  if (!iso) return "Time TBA";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function QuickStart({ onError }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [access, setAccess] = useState("member");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    if (!title.trim()) return toast.error("Give your broadcast a title.");
    setBusy(true);
    try {
      const { data } = await api.post("/creator/live/sessions", { title: title.trim(), description: "", access_level: access });
      navigate(`/creator/live/${data.id}/studio`);
    } catch (err) {
      onError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div data-testid="go-live-quickstart" className="rounded-2xl border border-[#d42be8]/40 bg-gradient-to-br from-[#2a0b3d]/70 to-[#0c0a10] p-5">
      <p className="flex items-center gap-2 text-sm font-semibold"><Radio size={16} className="text-[#d42be8]" /> Start a broadcast now</p>
      <p className="mt-1 text-xs text-[#a7a0af]">You'll preview your camera and microphone backstage first — nobody sees you until you tap Go Live.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_200px_auto]">
        <Input data-testid="go-live-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="Broadcast title" className="h-11 border-white/10 bg-white/5" />
        <Select value={access} onValueChange={setAccess}>
          <SelectTrigger data-testid="go-live-access" className="h-11 border-white/10 bg-white/5"><SelectValue /></SelectTrigger>
          <SelectContent>{ACCESS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={start} disabled={busy} data-testid="go-live-open-studio" className="btn-brand flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />} Open Go Live studio
        </button>
      </div>
    </div>
  );
}

export default function LiveControl() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", access_level: "subscriber", scheduled_for: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/creator/live/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Please enter a title.");
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.scheduled_for) payload.scheduled_for = new Date(payload.scheduled_for).toISOString();
      else delete payload.scheduled_for;
      await api.post("/creator/live/sessions", payload);
      toast.success("Session scheduled.");
      setOpen(false);
      setForm({ title: "", description: "", access_level: "subscriber", scheduled_for: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await api.delete(`/creator/live/sessions/${s.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const end = async (s) => {
    try {
      await api.put(`/creator/live/sessions/${s.id}`, { status: "ended" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="creator-live">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">Go Live</h1>
          <p className="text-sm text-[#a7a0af]">Broadcast live to your fans, or schedule a session for later.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="live-new-session-btn"
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus size={16} /> Schedule
        </button>
      </div>

      <div className="mt-6"><QuickStart onError={setErr} /></div>
      {err && <p data-testid="go-live-error" className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{err}</p>}

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div className="hidden rounded-2xl glass p-6 lg:col-span-2 lg:block">
          <div className="flex aspect-video items-center justify-center rounded-xl border border-white/10 bg-black/50">
            <div className="text-center">
              <Radio size={32} className="mx-auto text-[#6f6878]" />
              <p className="mt-2 text-sm font-semibold">Ready to broadcast</p>
              <p className="text-xs text-[#a7a0af]">Start a broadcast above, or tap “Open studio” on a scheduled session.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl glass p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Your sessions</h2>
          </div>
          {loading ? (
            <div className="mt-4 space-y-2">{[0, 1].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-white/5" />)}</div>
          ) : sessions.length === 0 ? (
            <p data-testid="live-sessions-empty" className="mt-4 text-center text-xs text-[#a7a0af]">No sessions yet.</p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {sessions.map((s) => (
                <li key={s.id} data-testid={`live-session-${s.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{s.title}</p>
                      <p className="flex items-center gap-1 text-[0.65rem] text-[#a7a0af]">
                        <CalendarClock size={11} /> {s.scheduled_for ? fmt(s.scheduled_for) : "Unscheduled"} · {s.status === "offline" ? "backstage" : s.status}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {s.status !== "ended" && (
                        <Link to={`/creator/live/${s.id}/studio`} data-testid={`live-open-studio-${s.id}`} className="rounded-md border border-[#d42be8]/50 bg-[#d42be8]/10 px-2 py-1 text-[0.6rem] font-semibold text-[#f0a8fa]">{s.status === "live" ? "Return to studio" : "Open studio"}</Link>
                      )}
                      {(s.status === "scheduled" || s.status === "offline") && (
                        <button onClick={() => end(s)} className="rounded-md border border-white/10 px-2 py-1 text-[0.6rem] hover:border-white/30">End</button>
                      )}
                      <button onClick={() => remove(s)} data-testid={`live-delete-${s.id}`} className="rounded-md border border-white/10 p-1.5 text-red-300 hover:border-red-400/40">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#0c0a10] text-white">
          <DialogHeader><DialogTitle>Schedule a live session</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <Label className="text-xs text-[#a7a0af]">Title</Label>
              <Input data-testid="live-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-white/10 bg-white/5" placeholder="Golden Hour — Live" />
            </div>
            <div>
              <Label className="text-xs text-[#a7a0af]">Description</Label>
              <Textarea data-testid="live-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-white/10 bg-white/5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#a7a0af]">Access</Label>
                <Select value={form.access_level} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                  <SelectTrigger data-testid="live-access-select" className="mt-1 border-white/10 bg-white/5"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCESS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#a7a0af]">Scheduled for</Label>
                <Input data-testid="live-schedule-input" type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} className="mt-1 border-white/10 bg-white/5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={create} disabled={busy} data-testid="live-create-btn" className="btn-brand w-full rounded-xl py-3 text-sm font-semibold text-white">
              {busy ? <Loader2 className="mx-auto animate-spin" size={16} /> : "Schedule session"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
